/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { createContext, useContext, useMemo } from 'react';
import type { Logger } from './interfaces';

const defaultLogger: Logger = {
  logEvent: () => {},
  logError: () => {},
  logScreenView: () => {},
  debug: (message, ...optionalParams) => {
    if (__DEV__) console.debug(message, ...optionalParams);
  },
  info: (message, ...optionalParams) => {
    if (__DEV__) console.info(message, ...optionalParams);
  },
  warn: (message, ...optionalParams) => {
    if (__DEV__) console.warn(message, ...optionalParams);
  },
  error: (message, ...optionalParams) => {
    if (__DEV__) console.error(message, ...optionalParams);
  },
};

export const LoggerContext = createContext<Logger>(defaultLogger);

export const useLogger = () => useContext(LoggerContext);

export const LoggerProvider = ({
  logger,
  children,
}: {
  logger?: Logger;
  children: React.ReactNode;
}) => {
  const providerValue = useMemo(
    () => ({
      ...defaultLogger,
      ...logger,
    }),
    [defaultLogger, logger],
  );
  return (
    <LoggerContext.Provider value={providerValue}>
      {children}
    </LoggerContext.Provider>
  );
};
